import React, { useCallback, useEffect, useState } from 'react';
import { TReviewSnippetSelectionPaneProps, TSnippetItem } from './types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { secureHttp } from '../../utils/request';
import { showToast, ToastType } from '../../utils/toast';
import { Button } from '../../components/common/button';
import { ReviewSnippetSelectionCard } from './review_snippet_selection_card';
import update from 'immutability-helper';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';

const ReviewSnippetSelectionPane = ({ 
    cards, 
    reviewShowcaseData, 
    selNHID, 
    setCards 
}: TReviewSnippetSelectionPaneProps) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const queryClient = useQueryClient();

    const { Org } = useSelector(
        ({ SessionState }: RootState) => SessionState
    );

    useEffect(() => {
        setIsSubmitting(false);
    }, [reviewShowcaseData]);

    const { mutate } = useMutation({
        mutationFn: async (values: TSnippetItem[]) => {
            const payload = {
                nhID: selNHID,
                reviewSnippets: values.filter(item => item.snippetText).map((item, index) => ({
                    ReviewID: item.reviewContext.sourceReviewID,
                    Snippet: item.snippetText,
                    Sequence: index + 1
                }))
            };

            await secureHttp.post(`/reviewshowcase/${selNHID}`, payload);
        },
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ['review', 'showcase', selNHID] });
        },
        onError: (err: { status: number, statusText: string }) => {
            setIsSubmitting(false);
            showToast(err.statusText, ToastType.Error);
        },
        onSuccess: () => {
            showToast('Review Showcase successfully updated - please note changes will take up to 1 hour to show on the website', ToastType.Success);
            queryClient.invalidateQueries({ queryKey: ['review', 'showcase', selNHID] });
            queryClient.invalidateQueries({ queryKey: ['review', 'showcase', Org.NHID] });
        }
    });

    const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
        setCards((prevCards: TSnippetItem[]) =>
            update(prevCards, {
            $splice: [
                [dragIndex, 1],
                [hoverIndex, 0, prevCards[dragIndex] as TSnippetItem],
            ],
            }),
        );
    }, [cards]);
  
    const removeCard = useCallback((cardItem: TSnippetItem) => {
        setCards((prevCards: TSnippetItem[]) => {
            const newCards = prevCards.filter(item => item.snippetID !== cardItem.snippetID);
            return newCards;
        });
    }, [cards]);

    const renderCard = useCallback(
        (card: TSnippetItem, index: number) => {
        return (
            <ReviewSnippetSelectionCard
              key={card.snippetID}
              index={index}
              id={card.snippetID}
              text={card.snippetText}
              reviewContext={card?.reviewContext}
              moveCard={moveCard}
              setCard={removeCard}
            />
          );
        },
        [cards],
    );

    const saveChanges = () => {
        setIsSubmitting(true);
        mutate(cards);
    };

    const hasDiff = cards.some((item, index) => item.snippetID !== reviewShowcaseData[index]?.Sequence || item.snippetText !== reviewShowcaseData[index]?.Snippet) || cards.length !== reviewShowcaseData.length;
    const hasErr = cards.some((item) => item?.errorContext?.hasError);

    return <>
        <div className="flex flex-row items-center justify-between">
            { cards.length > 1 ?
                <p className="text-brand_grey-medium">
                    (drag-and-drop to reorder)
                </p> : null
            }
            <Button 
                disabled={!hasDiff || hasErr || isSubmitting}
                isLoading={isSubmitting}
                onClick={saveChanges}
                className="ms-auto"
            >
                Save
            </Button>
        </div>
        <div className="mt-3 grid gap-3">
            {cards.map((card, i) => renderCard(card, i))}
        </div>
        
    </>;
};

export default ReviewSnippetSelectionPane;
